import type { PageContext } from "@redotech/redo-agents-common/page-context";
import File02Icon from "@redotech/redo-icons/file-02.svg";
import XCloseIcon from "@redotech/redo-icons/x-close.svg";
import { GlobalAgentContext } from "@redotech/redo-merchant-app-common/global-agent-context";
import { Tooltip } from "@redotech/redo-web/arbiter-components/tooltip/tooltip";
import { useBreadcrumbs } from "@redotech/redo-web/breadcrumb";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useContext } from "react";
import * as styles from "./ask-redo-page-context-chip.module.css";

/** Shown when neither `pageTitle` nor a route breadcrumb is available. */
const PAGE_CONTEXT_FALLBACK_LABEL = "This page";

/**
 * Notion-style page-context chip rendered above the Ask Redo prompt
 * input. Tells the merchant the agent knows what page they're viewing
 * ("📄 Return #1002") and lets them detach it with × before sending.
 *
 * The chip label IS the transparency — it names the page the agent can
 * read. The tooltip just labels the × affordance ("Remove context"); it
 * deliberately does NOT surface the raw `pageDescription` (written for
 * the model) or the structured `pageData` (full of internal IDs), which
 * read as noise to a merchant.
 *
 * Dismissal semantics live in `GlobalAgentContext`: × strips
 * `pageDescription`/`pageData` from every send until the merchant
 * navigates to another page, while skill routing, suggested questions,
 * and explicitly pinned references keep flowing.
 */
export const AskRedoPageContextChip = memo(function AskRedoPageContextChip() {
  const { basePageContext, pageContextDismissed, dismissPageContext } =
    useContext(GlobalAgentContext);
  const breadcrumbs = useBreadcrumbs();

  // Only render for contexts a page deliberately described. A context
  // carrying nothing but skill hints has nothing the merchant would
  // recognize as "the agent sees my page".
  if (pageContextDismissed || !describesPage(basePageContext)) {
    return null;
  }

  // Label preference: the page's explicit `pageTitle` (can carry entity
  // names like "Return #1002"), else the deepest route breadcrumb (the
  // browser-tab-title source — covers pages that predate `pageTitle`
  // with their real name and no per-page wiring), else a generic label.
  const breadcrumbName = breadcrumbs?.[breadcrumbs.length - 1]?.name?.trim();
  const label =
    basePageContext.pageTitle ?? breadcrumbName ?? PAGE_CONTEXT_FALLBACK_LABEL;

  return (
    // Sits above the textarea (and the pinned-references row), mirroring
    // that row's outer padding so the chips stack cleanly. No bottom
    // padding — it sits flush against the input below.
    <Flex gap="xs" pt="lg" px="lg" wrap="wrap">
      <Tooltip leftAlign title="Remove context">
        {/* Compact bg-tertiary pill matching the inventory agent chat's
            upload chip. */}
        <Flex align="center" className={styles.chip} gap="xs">
          <File02Icon aria-hidden="true" className={styles.icon} />
          <Text fontSize="xs">{label}</Text>
          <button
            aria-label="Remove page context"
            className={styles.remove}
            // Prevent the chip's parent `.ai-glow-focus` wrapper from
            // flashing its :focus-within glow when the merchant clicks ×
            // (same treatment as the pinned-reference chip's remove).
            onClick={dismissPageContext}
            onMouseDown={(e) => e.preventDefault()}
            type="button"
          >
            <XCloseIcon aria-hidden="true" className={styles.removeIcon} />
          </button>
        </Flex>
      </Tooltip>
    </Flex>
  );
});

/**
 * A context "describes a page" when it carries something the agent can
 * read about the page — a title, a description, or structured data.
 */
function describesPage(
  context: PageContext | undefined,
): context is PageContext {
  return Boolean(
    context &&
    (context.pageTitle || context.pageDescription || context.pageData),
  );
}
